import { cn } from '../../lib/cn';
import { usePageSeoContext } from '../../lib/PageSeoContext';
import {
  WHATSAPP_BUTTON_ENABLED_KEY,
  WHATSAPP_BUTTON_TEXT_KEY,
  DEFAULT_WHATSAPP_BUTTON_TEXT,
  TRUST_BADGE_ENABLED_KEY,
  TRUST_BADGE_SCRIPT_KEY,
} from '../../lib/siteSettingsApi';

/** Floating site-wide WhatsApp CTA — lower friction than the booking modal
 * for someone who's still deciding, since it opens a live chat with the
 * host instead of a form. Admin-controlled from Settings > Settings
 * (enable/disable + the prefilled message), reusing the WhatsApp number
 * already configured under Contact & Social. */
export default function WhatsAppButton() {
  const { siteSettings } = usePageSeoContext();
  const number = siteSettings.social_whatsapp;
  const enabled = siteSettings[WHATSAPP_BUTTON_ENABLED_KEY] !== '0';
  const message = siteSettings[WHATSAPP_BUTTON_TEXT_KEY] || DEFAULT_WHATSAPP_BUTTON_TEXT;
  const digits = number?.replace(/[^\d]/g, '') ?? '';

  // Trustindex-style trust badges are third-party embeds that render their
  // own fixed-position widget (typically the same bottom corner) — we can't
  // reach into that script to reposition it, so when it's also active we
  // move ourselves up out of its way instead of overlapping it.
  const trustBadgeActive = siteSettings[TRUST_BADGE_ENABLED_KEY] !== '0' && !!siteSettings[TRUST_BADGE_SCRIPT_KEY];

  // A non-numeric value (e.g. a username typed into the wrong field) strips
  // down to an empty string, producing a dead https://wa.me/ link with no
  // destination — so require actual digits, not just a truthy setting.
  if (!enabled || !digits) return null;

  const href = `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat with us on WhatsApp"
      className={cn(
        'hs-wa-pulse fixed right-5 md:right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center text-white shadow-[0_6px_20px_rgba(0,0,0,0.28)] transition-transform duration-200 hover:scale-105 active:scale-95',
        trustBadgeActive ? 'bottom-24 md:bottom-28' : 'bottom-5 md:bottom-6',
      )}
    >
      <svg viewBox="0 0 24 24" width={28} height={28} fill="currentColor" aria-hidden="true">
        <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.9-4.45 9.9-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2m0 1.67c2.2 0 4.26.86 5.82 2.42a8.19 8.19 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.24 8.23a8.24 8.24 0 0 1-4.19-1.15l-.3-.17-3.12.82.83-3.04-.2-.32a8.16 8.16 0 0 1-1.25-4.36c0-4.54 3.7-8.24 8.24-8.24m-3.51 3.27c-.16 0-.44.06-.68.32-.23.27-.88.87-.88 2.1s.9 2.42 1.03 2.58c.14.16 1.75 2.68 4.25 3.71 1.29.58 1.78.58 2.3.53.77-.1 1.64-.58 1.87-1.14.23-.55.23-1.02.17-1.12-.06-.1-.22-.17-.45-.28-.23-.12-1.45-.73-1.67-.83-.23-.07-.39-.11-.53.12-.15.22-.6.71-.73.85-.13.15-.27.16-.5.04-.23-.12-1.03-.39-1.98-1.24-.74-.66-1.23-1.48-1.38-1.7-.13-.23-.01-.36.11-.48.11-.11.24-.29.36-.44.12-.14.15-.25.23-.42.09-.17.04-.32-.02-.44-.06-.11-.53-1.34-.74-1.83-.19-.47-.39-.43-.54-.44-.14 0-.3-.1-.22-.1" />
      </svg>
    </a>
  );
}
